import type { Dispatch, SetStateAction } from "react"
import type { AgentInfo, Deal, DealSlotReward } from "@/contexts/auth-api/deal-types"
import { dealsTreatyClient } from "@/contexts/auth-api/treaty-client"

type DealRewardNormalizer = (raw: unknown) => DealSlotReward | undefined

interface DealApiDeps<TUser> {
  getToken: () => string | null
  getGeneratedAgencyId: () => string | null | undefined
  setUser: Dispatch<SetStateAction<TUser | null>>
  normalizeDealSlotRewardPayload: DealRewardNormalizer
}

interface SubmitDealResponse<TUser> {
  success: boolean
  message?: string
  user?: TUser
  deal?: Deal
}

interface ClaimDealSlotRewardResponse<TUser> {
  success: boolean
  message?: string
  user?: TUser
  reward?: unknown
  alreadyClaimed?: boolean
}

interface ClaimFreeSpinRewardResponse<TUser> {
  success: boolean
  message?: string
  user?: TUser
  reward?: unknown
}

interface GetDealsResponse {
  success: boolean
  message?: string
  deals?: Deal[]
}

interface GetAgencyAgentsResponse {
  success: boolean
  message?: string
  agents?: AgentInfo[]
}

interface DealFeedEntry {
  displayName: string
  pointsEarned: number
  createdAt: string
}

interface GetRecentDealFeedResponse {
  success: boolean
  message?: string
  deals?: DealFeedEntry[]
}

interface GetCasinoSlotConfigResponse {
  success: boolean
  message?: string
  slot?: {
    probabilities?: Array<{
      outcome: string
      payoutMultiplier: number
      probabilityPct: number
    }>
  }
}

interface TreatyResponse<T = unknown> {
  data?: T | null
  error?: {
    value?: unknown
  } | null
}

function pickTreatyPayload(value: unknown): unknown {
  if (!value || typeof value !== "object") return value

  const direct = value as Record<string, unknown>
  if (typeof direct.success === "boolean") return direct

  for (const nested of Object.values(direct)) {
    if (nested && typeof nested === "object" && typeof (nested as Record<string, unknown>).success === "boolean") {
      return nested
    }
  }

  return value
}

function unwrapTreatyResult<T extends { success: boolean; message?: string }>(
  response: TreatyResponse<unknown>,
  fallbackMessage: string
): T {
  const data = pickTreatyPayload(response.data)
  if (data && typeof data === "object") return data as T

  const errorValue = pickTreatyPayload(response.error?.value)
  if (errorValue && typeof errorValue === "object") {
    return errorValue as T
  }

  return { success: false, message: fallbackMessage } as T
}

export function createDealsApi<TUser>(deps: DealApiDeps<TUser>) {
  const submitDeal = async (memberId: string, onBehalfOfAgentId?: string): Promise<{ success: boolean; message?: string; deal?: Deal }> => {
    const token = deps.getToken()
    if (!token) {
      return { success: false, message: "Not authenticated" }
    }

    try {
      const body: { memberId: string; onBehalfOfAgentId?: string } = { memberId }
      if (onBehalfOfAgentId) {
        body.onBehalfOfAgentId = onBehalfOfAgentId
      }

      const response = await dealsTreatyClient.api.deals.post(body, {
        headers: {
          authorization: `Bearer ${token}`,
        },
      })
      const data = unwrapTreatyResult<SubmitDealResponse<TUser>>(response, "Failed to submit deal")

      if (data.success && data.user) {
        deps.setUser(data.user as TUser)
        return { success: true, message: data.message, deal: data.deal as Deal | undefined }
      }

      return { success: false, message: data.message || "Failed to submit deal" }
    } catch (error) {
      console.error("Submit deal error:", error)
      return { success: false, message: "Network error. Please try again." }
    }
  }

  const claimDealSlotReward = async (dealId: string): Promise<{ success: boolean; message?: string; reward?: DealSlotReward; alreadyClaimed?: boolean }> => {
    const token = deps.getToken()
    if (!token) {
      return { success: false, message: "Not authenticated" }
    }

    try {
      const response = await dealsTreatyClient.api.deals["slot-reward"].post({ dealId }, {
        headers: {
          authorization: `Bearer ${token}`,
        },
      })
      const data = unwrapTreatyResult<ClaimDealSlotRewardResponse<TUser>>(response, "Failed to claim deal slot reward")

      if (data.success && data.user) {
        deps.setUser(data.user as TUser)
      }

      return {
        success: Boolean(data.success),
        message: data.message,
        reward: deps.normalizeDealSlotRewardPayload(data.reward),
        alreadyClaimed: Boolean(data.alreadyClaimed),
      }
    } catch (error) {
      console.error("Claim deal slot reward error:", error)
      return { success: false, message: "Network error. Please try again." }
    }
  }

  const claimFreeSpinReward = async (): Promise<{ success: boolean; message?: string; reward?: DealSlotReward }> => {
    const token = deps.getToken()
    if (!token) {
      return { success: false, message: "Not authenticated" }
    }

    try {
      const response = await dealsTreatyClient.api.deals["free-spin-reward"].post(undefined, {
        headers: {
          authorization: `Bearer ${token}`,
        },
      })
      const data = unwrapTreatyResult<ClaimFreeSpinRewardResponse<TUser>>(response, "Failed to claim free spin reward")

      if (data.success && data.user) {
        deps.setUser(data.user as TUser)
      }

      return {
        success: Boolean(data.success),
        message: data.message,
        reward: deps.normalizeDealSlotRewardPayload(data.reward),
      }
    } catch (error) {
      console.error("Claim free spin reward error:", error)
      return { success: false, message: "Network error. Please try again." }
    }
  }

  const getDeals = async (): Promise<{ success: boolean; deals?: Deal[]; message?: string }> => {
    const token = deps.getToken()
    if (!token) {
      return { success: false, message: "Not authenticated" }
    }

    try {
      const response = await dealsTreatyClient.api.deals.get({
        headers: {
          authorization: `Bearer ${token}`,
        },
      })
      const data = unwrapTreatyResult<GetDealsResponse>(response, "Failed to get deals")

      if (data.success) {
        return { success: true, deals: data.deals as Deal[] | undefined }
      }

      return { success: false, message: data.message || "Failed to get deals" }
    } catch (error) {
      console.error("Get deals error:", error)
      return { success: false, message: "Network error. Please try again." }
    }
  }

  const getAgencyAgents = async (): Promise<{ success: boolean; agents?: AgentInfo[]; message?: string }> => {
    const token = deps.getToken()
    const agencyId = deps.getGeneratedAgencyId()
    if (!token || !agencyId) {
      return { success: false, message: "Not authenticated or not an agency" }
    }

    try {
      const response = await dealsTreatyClient.api.agency.agents.get({
        headers: {
          authorization: `Bearer ${token}`,
        },
        query: { agencyId },
      })
      const data = unwrapTreatyResult<GetAgencyAgentsResponse>(response, "Failed to get agents")

      if (data.success) {
        return { success: true, agents: data.agents as AgentInfo[] | undefined }
      }

      return { success: false, message: data.message || "Failed to get agents" }
    } catch (error) {
      console.error("Get agency agents error:", error)
      return { success: false, message: "Network error. Please try again." }
    }
  }

  const getRecentDealFeed = async (): Promise<{ success: boolean; deals?: DealFeedEntry[]; message?: string }> => {
    const token = deps.getToken()
    if (!token) {
      return { success: false, message: "Not authenticated" }
    }

    try {
      const response = await dealsTreatyClient.api.deals.feed.get({
        headers: { authorization: `Bearer ${token}` },
      })
      const data = unwrapTreatyResult<GetRecentDealFeedResponse>(response, "Failed to get recent deal feed")
      return { success: Boolean(data.success), deals: data.deals, message: data.message }
    } catch (error) {
      console.error("Get recent deal feed error:", error)
      return { success: false, message: "Network error" }
    }
  }

  const getCasinoSlotConfig = async (): Promise<{
    success: boolean
    message?: string
    slot?: {
      probabilities: Array<{
        outcome: string
        payoutMultiplier: number
        probabilityPct: number
      }>
    }
  }> => {
    const token = deps.getToken()
    if (!token) {
      return { success: false, message: "Not authenticated" }
    }

    try {
      const response = await dealsTreatyClient.api.deals["casino-slot-config"].get({
        headers: { authorization: `Bearer ${token}` },
      })
      const data = unwrapTreatyResult<GetCasinoSlotConfigResponse>(response, "Failed to get casino slot config")
      return {
        success: Boolean(data.success),
        message: data.message,
        slot: data.slot && Array.isArray(data.slot.probabilities)
          ? { probabilities: data.slot.probabilities }
          : undefined,
      }
    } catch (error) {
      console.error("Get casino slot config error:", error)
      return { success: false, message: "Network error. Please try again." }
    }
  }

  return {
    submitDeal,
    claimDealSlotReward,
    claimFreeSpinReward,
    getDeals,
    getAgencyAgents,
    getRecentDealFeed,
    getCasinoSlotConfig,
  }
}
